"use client"

import React, { createContext, useContext, useEffect, useState } from 'react'
import { 
  User, 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  signOut, 
  onAuthStateChanged
} from 'firebase/auth'
import { auth, googleProvider } from '../lib/firebase'
import { AccessDeniedModal } from '../landing/landing-v2/AccessDeniedModal'
import { useNavigate } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { API_BASE_URL } from '../../lib/config'

interface CredentialUser {
  username: string
}

interface DbUser {
  id: string
  email: string
  username: string
  first_name: string | null
  last_name: string | null
  profile_picture: string | null
  display_name: string
}

interface AuthContextType {
  user: User | null
  credentialUser: CredentialUser | null
  dbUser: DbUser | null
  loading: boolean
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  signInWithCredentials: (username: string, password: string) => Promise<{ ok: boolean; error?: string }>
  isSignedIn: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [credentialUser, setCredentialUser] = useState<CredentialUser | null>(() => {
    try {
      const stored = localStorage.getItem('aira-credential-user')
      return stored ? JSON.parse(stored) : null
    } catch { return null }
  })
  const [dbUser, setDbUser] = useState<DbUser | null>(() => {
    try {
      const stored = localStorage.getItem('aira-db-user')
      return stored ? JSON.parse(stored) : null
    } catch { return null }
  })
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false)
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('')
  
  const fetchDbUser = async () => {
    const res = await fetch(`${API_BASE_URL}me`, { credentials: 'include' })
    if (!res.ok) throw new Error('Failed to load user profile')
    const data = (await res.json()) as DbUser
    setDbUser(data)
    localStorage.setItem('aira-db-user', JSON.stringify(data))
    return data
  }


  useEffect(() => {
    // Check for redirect result first
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          // Handle redirect result if needed
        }
      })
      .catch((error) => {
        console.error('AuthProvider: Redirect error:', error)
      })

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Set user immediately for Firebase authentication
        setUser(user)
        
        // Authenticate with backend after Firebase sign-in
        try {
          const { authenticateWithBackend } = await import('../lib/authenticateWithBackend')
          const authResult = await authenticateWithBackend(user)
          
          if (authResult.ok) {
            // Set backend auth status for landing page
            localStorage.setItem('aira-backend-authed', 'true')
            // Hydrate DB-backed user profile for UI display
            try {
              await fetchDbUser()
            } catch (e) {
              console.error('Failed to fetch DB user after Firebase login:', e)
            }
            // Dispatch custom event to notify landing page
            window.dispatchEvent(new CustomEvent('aira-auth-completed'))
          } else {
            // Handle access denied errors
            if (authResult.error?.includes('Access denied') || authResult.error?.includes('Only @quantumleap.co.in')) {
              // Sign out the user from Firebase
              await signOut(auth)
              // Show user-friendly error message in modal
              setAccessDeniedMessage(authResult.error || 'Access denied. You do not have permission to access this application.')
              setShowAccessDeniedModal(true)
            }
          }
        } catch (backendError) {
          console.error('Backend authentication error:', backendError)
          // Keep user signed in but log the backend error
        }
      } else {
        setUser(null)
        localStorage.removeItem('aira-backend-authed')
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  // Credential (non-Firebase) flow: if backend session exists, hydrate DB user on load.
  useEffect(() => {
    const authed = localStorage.getItem('aira-backend-authed') === 'true'
    if (authed && credentialUser && !dbUser) {
      fetchDbUser().catch(() => {})
    }
  }, [credentialUser, dbUser])

  const signIn = async () => {
    try {
      setLoading(true)
      
      // Configure the Google provider
      googleProvider.setCustomParameters({
        prompt: 'select_account'
      })
      
      // Try popup first, fallback to redirect if popup fails
      try {
        await signInWithPopup(auth, googleProvider)
        // Backend authentication is handled in onAuthStateChanged
        
      } catch (popupError: unknown) {
        const error = popupError as { code?: string }
        
        if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
          // Use redirect as fallback
          await signInWithRedirect(auth, googleProvider)
          // Don't set loading to false here as we're redirecting
          return
        } else {
          throw popupError
        }
      }
      
    } catch (error: unknown) {
      const authError = error as { code?: string; message?: string }
      
      // Handle specific error cases
      if (authError.code === 'auth/popup-closed-by-user') {
        // User closed the popup - no action needed
      } else if (authError.code === 'auth/popup-blocked') {
        alert('Please allow popups for this site and try again.')
      } else if (authError.code === 'auth/cancelled-popup-request') {
        // Another popup request is in progress - no action needed
      } else {
        alert('Authentication failed: ' + (authError.message || 'Unknown error'))
      }
    } finally {
      setLoading(false)
    }
  }

  const signOutUser = async () => {
    try {
      setLoading(true)
      await signOut(auth)
      setCredentialUser(null)
      setDbUser(null)
      localStorage.removeItem('aira-credential-user')
      localStorage.removeItem('aira-backend-authed')
      localStorage.removeItem('aira-db-user')
      localStorage.removeItem('aira-initial-chat-created')
      localStorage.removeItem('aira-welcome-seen')
    } catch (error) {
      console.error('Error signing out:', error)
    } finally {
      setLoading(false)
    }
  }

  const signInWithCredentials = async (username: string, password: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const API_BASE = import.meta.env.VITE_API_BASE_URL || ''
      const body = new URLSearchParams()
      body.append('username', username)
      body.append('password', password)

      // Step 1: call /login — sets the session cookie on success (302 redirect)
      await fetch(`${API_BASE}login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        credentials: 'include',
        redirect: 'manual',
      })

      // Step 2: verify by hitting a protected endpoint
      const verify = await fetch(`${API_BASE}chat/sessions`, {
        credentials: 'include',
      })

      if (verify.ok) {
        const cu: CredentialUser = { username }
        setCredentialUser(cu)
        localStorage.setItem('aira-credential-user', JSON.stringify(cu))
        localStorage.setItem('aira-backend-authed', 'true')
        try {
          await fetchDbUser()
        } catch (e) {
          console.error('Failed to fetch DB user after credential login:', e)
        }
        return { ok: true }
      }

      return { ok: false, error: 'Invalid username or password.' }
    } catch {
      return { ok: false, error: 'Network error. Please try again.' }
    }
  }

  const isSignedIn = !!user || !!credentialUser

  const value = {
    user,
    credentialUser,
    dbUser,
    loading,
    signIn,
    signOut: signOutUser,
    signInWithCredentials,
    isSignedIn
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AccessDeniedModal 
        isOpen={showAccessDeniedModal}
        onClose={() => setShowAccessDeniedModal(false)}
        message={accessDeniedMessage}
      />
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Helper components for easier migration
export function SignedIn({ children }: { children: React.ReactNode }) {
  const { isSignedIn, loading } = useAuth()
  if (loading) return null
  return isSignedIn ? <>{children}</> : null
}

export function SignedOut({ children }: { children: React.ReactNode }) {
  const { isSignedIn, loading } = useAuth()
  if (loading) return null
  return !isSignedIn ? <>{children}</> : null
}

interface AdminStatus {
  is_admin: boolean
  user_email: string | null
  user_role: string | null
}

export function UserButton({ afterSignOutUrl, showName = true, size = 'default', disableHover = false, dropdownPosition = 'below' }: { 
  afterSignOutUrl?: string
  showName?: boolean
  size?: 'default' | 'mini'
  disableHover?: boolean
  dropdownPosition?: 'above' | 'below'
}) {
  const { user, credentialUser, dbUser, signOut } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [adminStatus, setAdminStatus] = useState<AdminStatus | null>(null)
  const navigate = useNavigate()
  
  const displayName = dbUser?.display_name || dbUser?.username || credentialUser?.username || 'User'
  const profilePic = dbUser?.profile_picture || user?.photoURL || ''
  const emailForAdmin = user?.email || dbUser?.email || null

  // Check admin status from database
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!emailForAdmin) return

      try {
        const response = await fetch(`${API_BASE_URL}admin/check-status`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'x-user-email': emailForAdmin
          }
        })

        if (response.ok) {
          const status = await response.json()
          setAdminStatus(status)
        }
      } catch (error) {
        console.error('Error checking admin status:', error)
      }
    }

    checkAdminStatus()
  }, [emailForAdmin])

  const isAdmin = adminStatus?.is_admin || false
  
  if (!user && !credentialUser) {
    return null
  }

  const handleSignOut = async () => {
    await signOut()
    setIsOpen(false)
    // Navigate to landing page after sign out
    if (afterSignOutUrl) {
      window.location.href = afterSignOutUrl
    }
  }

  const handleButtonClick = () => {
    console.log('UserButton clicked, isOpen:', isOpen)
    setIsOpen(!isOpen)
  }

  const handleAdminClick = () => {
    setIsOpen(false)
    navigate('/admin')
  }

  if (size === 'mini') {
    return (
      <div className="relative group">
        <button
          onClick={handleButtonClick}
          className={`w-7 h-7 p-0 rounded-lg hover:bg-light-accent/50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer ${isOpen ? 'bg-light-accent/50 dark:bg-slate-800/50' : ''}`}
          title={displayName}
        >
          <img
            src={profilePic}
            alt={displayName}
            className="w-full h-full rounded-lg object-cover"
          />
        </button>
        
        {/* Dropdown menu */}
        {isOpen && (
          <div className={`absolute w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 z-[9999] ${
            dropdownPosition === 'above' 
              ? 'bottom-full right-0 mb-2' 
              : 'top-full right-0 mt-2'
          }`}>
            <div className="p-3 border-b border-gray-200 dark:border-slate-700">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {displayName}
              </p>
              {dbUser?.email && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={dbUser.email}>
                  {dbUser.email}
                </p>
              )}
            </div>
            {isAdmin && (
              <button
                onClick={handleAdminClick}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 border-b border-gray-200 dark:border-slate-700"
              >
                <Shield className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                Admin Panel
              </button>
            )}
            <button
              onClick={handleSignOut}
              className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-b-lg"
            >
              Sign Out
            </button>
          </div>
        )}
        
        {/* Click outside to close */}
        {isOpen && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
        )}
      </div>
    )
  }

  return (
    <div className="relative group">
      <button
        onClick={handleButtonClick}
        className={`flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer ${!disableHover ? 'hover:bg-light-accent/50 dark:hover:bg-slate-800/50' : ''} ${isOpen ? 'bg-light-accent/50 dark:bg-slate-800/50' : ''}`}
      >
        <img
          src={profilePic}
          alt={displayName}
          className="w-7 h-7 rounded-lg object-cover"
        />
        {showName && (
          <span className="text-sm font-medium text-light-text dark:text-white">
            {displayName}
          </span>
        )}
      </button>
      
      {/* Dropdown menu */}
      {isOpen && (
        <div className={`absolute w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 z-[9999] ${
          dropdownPosition === 'above' 
            ? 'bottom-full right-0 mb-2' 
            : 'top-full right-0 mt-2'
        }`}>
          <div className="p-3 border-b border-gray-200 dark:border-slate-700">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {displayName}
            </p>
            {dbUser?.email && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={dbUser.email}>
                {dbUser.email}
              </p>
            )}
          </div>
          {isAdmin && (
            <button
              onClick={handleAdminClick}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 border-b border-gray-200 dark:border-slate-700"
            >
              <Shield className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              Admin Panel
            </button>
          )}
          <button
            onClick={handleSignOut}
            className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-b-lg"
          >
            Sign Out
          </button>
        </div>
      )}
      
      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

export function SignInButton({ children }: { children: React.ReactNode }) {
  const { signIn, loading } = useAuth()
  
  const handleClick = () => {
    signIn()
  }
  
  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="inline-flex items-center justify-center"
    >
      {children}
    </button>
  )
}
